import React, { useState } from 'react';
import { Button, Col, Form, Modal, Radio, Row, notification } from 'antd';
import moment from 'moment';

import {
  ApiApplication,
  Client,
  Dataset,
  IdPackageType,
} from '@cbinsights/externalapiservice/externalapiservice';
import { useUpdateClient } from 'client/modules/external-api/hooks/queries/external-api';
import { useQueryClient } from 'react-query';
import { dateFormat } from './constants';

import {
  APIClientForm,
  APIClientFormField,
  ApplicationFormItem,
  DatasetConfigRow,
  DatasetsRow,
  InputsRow,
  PackageTypeRow,
  onFormClientValuesChange,
} from './CreateClientButton';

const LegacyRow = () => {
  return (
    <Row gutter={16} className="mt-2">
      <Col span={12} className="form-item-borderless">
        <Form.Item
          name={APIClientFormField.packageType}
          label="Package Type"
          style={{ marginBottom: 'none' }}
        >
          <Radio.Group className="mt-2">
            <Radio value={IdPackageType.LEGACY_PER_REQUEST}>Legacy</Radio>
          </Radio.Group>
        </Form.Item>
      </Col>
    </Row>
  );
};

const sameDatasets = (a: Dataset[], b: Dataset[]) =>
  a.length === b.length && a.every((d) => b.includes(d));

const deriveIdPackageType = (v: Partial<APIClientForm>) =>
  v.includeV2API && !v.packageType ? IdPackageType.V2_ONLY : v.packageType;

const buildUpdateMaskPaths = (
  values: APIClientForm,
  initial: Partial<APIClientForm>
): string[] => {
  const has = (k: keyof APIClientForm) => Object.hasOwn(values, k);
  const paths: string[] = [];
  if (
    (has('packageType') || has('includeV2API')) &&
    deriveIdPackageType(values) !== deriveIdPackageType(initial)
  ) {
    paths.push('id_package_type');
  }
  if (
    has('expirationDate') &&
    values.expirationDate?.format(dateFormat) !==
      initial.expirationDate?.format(dateFormat)
  ) {
    paths.push('expiration_date');
  }
  const v2UncheckedOnPerOrg =
    initial.includeV2API === true &&
    values.includeV2API === false &&
    (values.packageType === IdPackageType.ESSENTIAL_PER_ORG ||
      values.packageType === IdPackageType.EXPERT_PER_ORG);
  if (
    (has('datasets') &&
      !sameDatasets(values.datasets ?? [], initial.datasets ?? [])) ||
    v2UncheckedOnPerOrg
  ) {
    paths.push('datasets');
  }
  if (
    has('srAllowedOverage') &&
    values.srAllowedOverage !== initial.srAllowedOverage
  ) {
    paths.push('scouting_report_allowed_overage');
  }
  if (
    has('srCreditsAllocation') &&
    values.srCreditsAllocation !== initial.srCreditsAllocation
  ) {
    paths.push('scouting_report_credits_allocation');
  }
  if (
    has('chatCBICreditsAllocation') &&
    values.chatCBICreditsAllocation !== initial.chatCBICreditsAllocation
  ) {
    paths.push('chat_cbi_credits_allocation');
  }
  if (
    has('chatCBIAllowedOverage') &&
    values.chatCBIAllowedOverage !== initial.chatCBIAllowedOverage
  ) {
    paths.push('chat_cbi_allowed_overage');
  }
  if (has('application') && values.application !== initial.application) {
    paths.push('id_api_application');
  }
  return paths;
};

const EditClientButton = ({
  className,
  client,
}: {
  className?: string;
  client: Client;
}): React.ReactElement => {
  const [form] = Form.useForm<APIClientForm>();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const queryClient = useQueryClient();

  const initialValues: Partial<APIClientForm> = {
    includeV2API: client.datasets.length > 0,
    packageType:
      client.id_package_type !== IdPackageType.V2_ONLY
        ? client.id_package_type
        : undefined,
    expirationDate: moment.utc(client.expiration_date),
    datasets: client.datasets,
    srAllowedOverage: Number(client.scouting_report_allowed_overage ?? 0),
    srCreditsAllocation: Number(client.scouting_report_credits_allocation ?? 0),
    chatCBICreditsAllocation: Number(client.chat_cbi_credits_allocation ?? 0),
    chatCBIAllowedOverage: Number(client.chat_cbi_allowed_overage ?? 0),
    application: client.api_application,
  };

  const watched = Form.useWatch<APIClientForm>([], form);
  const paths = watched ? buildUpdateMaskPaths(watched, initialValues) : [];

  const closeModal = () => {
    setIsModalOpen(false);
    form.resetFields();
  };

  const { error, isLoading, mutateAsync } = useUpdateClient();

  const handleSubmit = async (values: APIClientForm) => {
    const idPackageType = deriveIdPackageType(values);

    if (!idPackageType) {
      notification.error({
        message: 'Package Type or v2 is required',
      });
      return;
    }

    const updatedClient: Partial<Client> = {
      name: String(client.name),
      client_id: client.client_id,
      id_package_type: idPackageType,
      expiration_date: values.expirationDate.format(dateFormat),
      datasets: values.datasets,
      scouting_report_allowed_overage: values.srAllowedOverage,
      scouting_report_credits_allocation: values.srCreditsAllocation,
      chat_cbi_credits_allocation: values.chatCBICreditsAllocation,
      chat_cbi_allowed_overage: values.chatCBIAllowedOverage,
      api_application: values.application,
    };

    await mutateAsync({ client: updatedClient, paths });
    await queryClient.invalidateQueries([
      'externalapiservice/ListTeamClients',
      Number(client.name),
    ]);
    setIsModalOpen(false);
  };

  return (
    <>
      <Button
        className={className}
        onClick={() => {
          setIsModalOpen(true);
        }}
        type="ghost"
      >
        Edit
      </Button>
      <Modal
        title={`Edit API Client for ${client.team_name}`}
        okButtonProps={{
          disabled: isLoading || paths.length === 0,
          loading: isLoading,
        }}
        onOk={() => {
          form.submit();
        }}
        onCancel={closeModal}
        open={isModalOpen}
      >
        <Form
          layout="vertical"
          form={form}
          onValuesChange={onFormClientValuesChange(form)}
          onFinish={handleSubmit}
          initialValues={initialValues}
        >
          {client.id_package_type !== IdPackageType.LEGACY_PER_REQUEST ? (
            <>
              <PackageTypeRow
                hideV1Options={
                  client.api_application !==
                  ApiApplication.API_APPLICATION_UNSPECIFIED
                }
                disableV2Check={
                  client.api_application !==
                  ApiApplication.API_APPLICATION_UNSPECIFIED
                }
              />
              <InputsRow includeCredits={false} />
              <ApplicationFormItem disableApplication />
              <DatasetsRow />
              <DatasetConfigRow />
            </>
          ) : (
            <>
              <LegacyRow />
              <InputsRow includeCredits={false} />
            </>
          )}
        </Form>
        {error && <p className="text-red-600 mt-4">Something went wrong.</p>}
      </Modal>
    </>
  );
};

export default EditClientButton;
